import Input from "@/app/components/ui/Inputs/Input";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { Controller, FieldError } from "react-hook-form";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import CreatableSelect from "react-select/creatable";
import { FiEye } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { LuUpload } from "react-icons/lu";
import { FiPlusCircle } from "react-icons/fi";
import BarcodeViewModal from "../BarcodeViewModal";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { format } from "date-fns";
import { Loader } from "lucide-react";
import {
  logicType,
  rtvAllowed,
  prescriptionType,
  Props,
} from "@components/routes/auth_routes/InventoryManagement/ProductSetupComponent/ProductAddComponent/GeneralDetailsFormComponent/types";
import { useGeneralDetailsForm } from "./useGeneralDetailsForm";
import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";

export type {
  IInventoryBatchFormFields,
  IInventoryFormFields,
} from "@components/routes/auth_routes/InventoryManagement/ProductSetupComponent/ProductAddComponent/GeneralDetailsFormComponent/types";

const GeneralDetailsFormComponent = ({ formHook, onParentTitleIdChange }: Props) => {
  const {
    forOnlyNumber,
    forAllAcceptWithOutStartWithDotAndSpace,
    forOnlyNumbersWithDecimal,
    fetchParentTitleList,
    productDetails,
    couponSubCategory,
    couponCategoryItem,
    couponSubCategoryItem,
    brandItemsList,
    animalItemsList,
    discountItemsList,
    taxListItems,
    medicineConsumptionListItems,
    isOpen,
    setIsOpen,
    fields,
    fileInputRefs,
    lotFields,
    fileInputRefsLot,
    selectedImage,
    setSelectedImage,
    editableFields,
    loader,
    formattedTags,
    isEditMode,
    handleFileChange,
    handleUploadClick,
    addField,
    deleteField,
    handleLotFileChange,
    handleLotUploadClick,
    addLotField,
    deleteLotField,
    onSubmit,
    validateInwardTaxPair,
    validateOutwardTaxPair,
    triggerInwardTaxValidation,
    triggerOutwardTaxValidation,
    validateTaxSum,
    getTaxSectionErrorMessage,
    customStyles,
    handelCreateItems,
    handleParentTitleSelect,
    clearMedicineFields,
    shouldEnableParentLinkedFields,
  } = useGeneralDetailsForm({ formHook, onParentTitleIdChange });

  // useFormValidations returns RegisterOptions<any, string>, which doesn't line up with
  // formHook.register's field-literal generic; relax the types at the call site only.
  const register = formHook.register as (name: any, options?: any) => ReturnType<typeof formHook.register>;

  return (
    <div className="p-3">
      <div>
        <div className="mt-2">
          <h2 className="text-sm font-semibold">Product Details</h2>
          <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
            <div className="mt-4 flex flex-col gap-4">
              <div>
                <div>
                  <div className="w-full">
                    <p>
                      Does this variant already have a product created for it?
                      <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="mt-3">
                    <RadioGroupComponent
                      alignment="horizontal"
                      errorMessages={[]}
                      fieldError={formHook.formState.errors?.isproductParent}
                      register={register("isproductParent")}
                      groupOptions={[
                        { id: "yes", title: "Yes", colorClassName: "text-green-500" },
                        { id: "no", title: "No", colorClassName: "text-red-500" },
                      ]}
                      defaultSelectedId={formHook.watch("isproductParent")}
                      onRadioChange={(optionId: string) => {
                        formHook.setValue("isproductParent", optionId as "yes" | "no");
                      }}
                      radioOptionClassName="text-sm w-28"
                    />
                  </div>
                </div>
                {formHook.watch("isproductParent") === "yes" && (
                  <div className="flex flex-col gap-2">
                    <div className="mt-5">
                      <div>
                        <p>
                          Product Name <span className="text-red-400">*</span>
                        </p>
                      </div>
                      <div className="w-full sm:w-2/6 mt-2">
                        <SearchableSelectMenu
                          placeholder="e.g. Select Product Name"
                          errorMessages={[
                            {
                              type: "required",
                              message: "Required",
                            },
                          ]}
                          fieldError={formHook.formState.errors?.parentTitle ?? null}
                          register={register("parentTitle", {
                            required: true,
                          })}
                          onSelectItem={handleParentTitleSelect}
                          withAPISearch={{
                            apiCallback: fetchParentTitleList,
                            reducerIdKey: "vetic_product_id",
                            reducerTitleKey: "supertails_name",
                          }}
                          defaultSelectedItemForAPISearch={{
                            id: formHook.watch("parentTitleId"),
                            title: formHook.watch("parentTitle"),
                          }}
                          showDropdownIcon
                          maxLength={50}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {formHook.watch("isproductParent") === "no" && (
                  <div className="mt-5">
                    <div>
                      <p>
                        Product Name <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div className="w-full sm:w-2/6 mt-2">
                      <Input
                        className="text-xs"
                        placeholder="e.g. Royal Canin Persian Adult"
                        register={register("parent_name", {
                          required: true,
                          ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                        })}
                        fieldError={formHook.formState.errors?.parent_name}
                        errorMessages={[
                          forAllAcceptWithOutStartWithDotAndSpace.errors,
                          { message: "Parent Name is required", type: "required" },
                        ]}
                        maxLength={100}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div>
                  <p>
                    Pet Species <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Animal Type is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("animalType", item?.title);
                      }
                    }}
                    fieldError={formHook.formState.errors.animalType}
                    register={register("animalType", { required: true })}
                    selectItems={animalItemsList}
                    placeholder="e.g. Dog"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    withDropDownSearch
                    defaultSelected={animalItemsList?.filter((oc) => oc?.title === formHook.watch("animalType"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                    disabled={!shouldEnableParentLinkedFields && editableFields.animalType === false}
                  />
                </div>
              </div>
              <div>
                <div>
                  <p>
                    Category <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Category is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("vmscategory", item?.title);
                        formHook.setValue("vmscategoryId", item?.id);
                        formHook.setValue("vmsSubcategory", "");
                        formHook.setValue("vmsSubcategoryId", "");
                        formHook.clearErrors("vmsSubcategory");
                        clearMedicineFields();
                      } else {
                        formHook.setValue("vmscategory", "");
                        formHook.setValue("vmscategoryId", "");
                        formHook.setValue("vmsSubcategory", "");
                        formHook.setValue("vmsSubcategoryId", "");
                        formHook.clearErrors("vmsSubcategory");
                        clearMedicineFields();
                      }
                    }}
                    fieldError={formHook.formState.errors.vmscategory}
                    register={register("vmscategory", { required: true })}
                    selectItems={couponCategoryItem}
                    placeholder="Select Category"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    withDropDownSearch
                    defaultSelected={couponCategoryItem?.filter((oc) => oc?.id === formHook.watch("vmscategoryId"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                    disabled={!shouldEnableParentLinkedFields && editableFields.vmscategory === false}
                  />
                </div>
              </div>
              <div>
                <div>
                  <p>
                    Sub-Category <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Sub-category is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("vmsSubcategory", item?.title);
                        formHook.setValue("vmsSubcategoryId", item?.id);
                        const selectedSubcategory = couponSubCategory?.find(
                          (cat: any) => cat?.subcategory_id === item?.id,
                        );
                        if (!selectedSubcategory?.is_medicine) {
                          clearMedicineFields();
                        }
                      } else {
                        formHook.setValue("vmsSubcategory", "");
                        formHook.setValue("vmsSubcategoryId", "");
                        clearMedicineFields();
                      }
                    }}
                    fieldError={formHook.formState.errors.vmsSubcategory}
                    register={register("vmsSubcategory", { required: true })}
                    selectItems={couponSubCategoryItem}
                    placeholder="Select Sub-Category"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    withDropDownSearch
                    defaultSelected={
                      couponSubCategoryItem?.filter((oc) => oc?.id === formHook.watch("vmsSubcategoryId"))[0]
                    }
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                    disabled={!shouldEnableParentLinkedFields && editableFields.vmsSubcategory === false}
                  />
                </div>
              </div>
              {couponSubCategory?.some(
                (cat: any) => cat?.subcategory_id === formHook.watch("vmsSubcategoryId") && cat?.is_medicine,
              ) && (
                <div>
                  <div>
                    <p>
                      Prescription Type <span className="text-red-400">*</span>{" "}
                    </p>
                  </div>
                  <div className="w-full sm:w-2/6 mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Prescription type is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("prescription_type", item?.title);
                        } else {
                          formHook.setValue("prescription_type", "");
                        }
                      }}
                      fieldError={(formHook.formState.errors?.prescription_type as FieldError) ?? null}
                      register={register("prescription_type", { required: true })}
                      selectItems={prescriptionType}
                      placeholder="Select Prescription Type"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={
                        prescriptionType.filter(
                          (oc) =>
                            oc?.title?.trim().toLowerCase() ===
                            String(formHook.watch("prescription_type") ?? "")
                              .trim()
                              .toLowerCase(),
                        )[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              )}
              <div>
                <div>
                  <p>
                    Brand <span className="text-red-400">*</span>{" "}
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Brand is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("brand", item?.title);
                        formHook.setValue("brandId", item?.id);
                      }
                    }}
                    fieldError={formHook.formState.errors?.brand}
                    register={register("brand", { required: true })}
                    selectItems={brandItemsList}
                    placeholder="Select Brand"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    defaultSelected={brandItemsList?.filter((oc) => oc?.title === formHook.watch("brand"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                    disabled={!shouldEnableParentLinkedFields && editableFields.brand === false}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 sm:items-center">
                <div>
                  <div>
                    <p>RTV Allowed</p>
                  </div>
                  <div className="w-full sm:w-60 mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("rtv", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors?.rtv}
                      register={register("rtv", { required: false })}
                      selectItems={rtvAllowed}
                      placeholder="Select Yes/No"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={rtvAllowed?.filter((oc) => oc?.title === formHook.watch("rtv"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                      disabled={!shouldEnableParentLinkedFields && editableFields.rtv === false}
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <p>RTV Days</p>
                  </div>
                  <div className="w-full sm:w-60 mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. 30"
                      register={register("rtvDays", {
                        required: false,
                        ...forOnlyNumber.validations,
                      })}
                      fieldError={formHook.formState.errors?.rtvDays}
                      errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                      disabled={!shouldEnableParentLinkedFields && editableFields.rtvDays === false}
                    />
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <p>Warehouse Margin</p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <Input
                    // type="number"
                    placeholder="e.g. 2"
                    register={register("tams_margin", {
                      required: false,
                      ...forOnlyNumber.validations,
                    })}
                    fieldError={formHook.formState.errors?.tams_margin}
                    errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                    disabled={!shouldEnableParentLinkedFields && editableFields.tams_margin === false}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold">
                  Inward Tax Details <span className="text-red-400">*</span>
                </p>
                <div className="pt-2">
                  <p>
                    HSN Code <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "HSN Code is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("hsncode", item.title);
                      }
                    }}
                    fieldError={formHook.formState.errors?.hsncode}
                    register={register("hsncode", {
                      required: true,
                    })}
                    selectItems={taxListItems}
                    placeholder="Select HSN Code"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    withDropDownSearch
                    defaultSelected={taxListItems?.filter((oc) => oc?.title === formHook.watch("hsncode"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                    showDropdownWhenNoData={true}
                    disabled={!shouldEnableParentLinkedFields && editableFields.hsncode === false}
                  />
                </div>
              </div>
              <div>
                <div></div>
                <div className="w-full sm:w-2/4 grid grid-cols-2 gap-4 mt-2 sm:flex sm:gap-4">
                  <div>
                    <div>
                      <p>
                        CGST <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div>
                      <Input
                        className="text-xs w-full sm:w-28"
                        placeholder="CGST %"
                        register={register("cgst", {
                          required: true,
                          ...forOnlyNumbersWithDecimal.validations,
                          max: {
                            value: 50,
                            message: "Tax % cannot be greater than 50",
                          },
                          validate: {
                            taxPair: validateInwardTaxPair,
                          },
                          onChange: triggerInwardTaxValidation,
                        })}
                        fieldError={formHook.formState.errors?.cgst}
                        errorMessages={[
                          { message: "CGST % is required", type: "required" },
                          forOnlyNumbersWithDecimal.errors,
                          { message: "Tax % cannot be greater than 50", type: "max" },
                        ]}
                        disabled={!shouldEnableParentLinkedFields && editableFields.cgst === false}
                      />
                    </div>
                  </div>
                  <div>
                    <div>
                      <p>
                        SGST <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div>
                      <Input
                        className="text-xs w-full sm:w-28"
                        placeholder="SGST %"
                        register={register("sgst", {
                          required: true,
                          ...forOnlyNumbersWithDecimal.validations,
                          max: {
                            value: 50,
                            message: "Tax % cannot be greater than 50",
                          },
                          validate: { taxPair: validateInwardTaxPair },
                          onChange: triggerInwardTaxValidation,
                        })}
                        fieldError={formHook.formState.errors?.sgst}
                        errorMessages={[
                          { message: "SGST % is required", type: "required" },
                          forOnlyNumbersWithDecimal.errors,
                          { message: "Tax % cannot be greater than 50", type: "max" },
                        ]}
                        disabled={!shouldEnableParentLinkedFields && editableFields.sgst === false}
                      />
                    </div>
                  </div>
                  <div>
                    <div>
                      <p>
                        IGST <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div>
                      <Input
                        className="text-xs w-full sm:w-28"
                        placeholder="IGST %"
                        register={register("igst", {
                          required: true,
                          ...forOnlyNumbersWithDecimal.validations,
                          max: {
                            value: 50,
                            message: "Tax % cannot be greater than 50",
                          },
                          validate: {
                            taxSum: () => validateTaxSum("inward", "igst"),
                          },
                          onChange: triggerInwardTaxValidation,
                        })}
                        fieldError={formHook.formState.errors?.igst}
                        errorMessages={[
                          { message: "IGST % is required", type: "required" },
                          forOnlyNumbersWithDecimal.errors,
                          { message: "Tax % cannot be greater than 50", type: "max" },
                        ]}
                        disabled={!shouldEnableParentLinkedFields && editableFields.igst === false}
                      />
                    </div>
                  </div>
                  <div>
                    <div>
                      <p>
                        UTGST <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div>
                      <Input
                        className="text-xs w-full sm:w-28"
                        placeholder="UTGST %"
                        register={register("ugst", {
                          required: true,
                          ...forOnlyNumbersWithDecimal.validations,
                          max: {
                            value: 50,
                            message: "Tax % cannot be greater than 50",
                          },
                          validate: {
                            taxSum: () => validateTaxSum("inward", "ugst"),
                          },
                          onChange: triggerInwardTaxValidation,
                        })}
                        fieldError={formHook.formState.errors?.ugst}
                        errorMessages={[
                          { message: "UTGST % is required", type: "required" },
                          forOnlyNumbersWithDecimal.errors,
                          { message: "Tax % cannot be greater than 50", type: "max" },
                        ]}
                        disabled={!shouldEnableParentLinkedFields && editableFields.ugst === false}
                      />
                    </div>
                  </div>
                </div>
                {getTaxSectionErrorMessage("inward") && (
                  <p className="text-xs text-red-500 mt-2">{getTaxSectionErrorMessage("inward")}</p>
                )}
              </div>
              <div>
                <div>
                  <p className="text-sm font-semibold">Outward Tax Details</p>
                  <div>
                    <p className="pt-2">
                      HSN Code <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-2/6 mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "HSN Code is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("outward_hsncode", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors?.outward_hsncode}
                      register={register("outward_hsncode", {
                        required: true,
                      })}
                      selectItems={taxListItems}
                      placeholder="Select HSN Code"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      defaultSelected={taxListItems?.filter((oc) => oc?.title === formHook.watch("outward_hsncode"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                      showDropdownWhenNoData={true}
                      disabled={editableFields.outward_hsncode === false}
                    />
                  </div>
                </div>
                <div>
                  <div></div>
                  <div className="w-full sm:w-2/4 grid grid-cols-2 gap-4 mt-2 sm:flex sm:gap-4">
                    <div>
                      <div>
                        <p>
                          CGST <span className="text-red-400">*</span>
                        </p>
                      </div>
                      <div>
                        <Input
                          className="text-xs w-full sm:w-28"
                          placeholder="CGST %"
                          register={register("outward_cgst", {
                            required: true,
                            ...forOnlyNumbersWithDecimal.validations,
                            max: {
                              value: 50,
                              message: "Tax % cannot be greater than 50",
                            },
                            validate: {
                              taxPair: validateOutwardTaxPair,
                            },
                            onChange: triggerOutwardTaxValidation,
                          })}
                          fieldError={formHook.formState.errors?.outward_cgst}
                          errorMessages={[
                            { message: "CGST % is required", type: "required" },
                            forOnlyNumbersWithDecimal.errors,
                            { message: "Tax % cannot be greater than 50", type: "max" },
                          ]}
                          disabled={editableFields.outward_cgst === false}
                        />
                      </div>
                    </div>
                    <div>
                      <div>
                        <p>
                          SGST <span className="text-red-400">*</span>
                        </p>
                      </div>
                      <div>
                        <Input
                          className="text-xs w-full sm:w-28"
                          placeholder="SGST %"
                          register={register("outward_sgst", {
                            required: true,
                            ...forOnlyNumbersWithDecimal.validations,
                            max: {
                              value: 50,
                              message: "Tax % cannot be greater than 50",
                            },
                            validate: {
                              taxPair: validateOutwardTaxPair,
                            },
                            onChange: triggerOutwardTaxValidation,
                          })}
                          fieldError={formHook.formState.errors?.outward_sgst}
                          errorMessages={[
                            { message: "SGST % is required", type: "required" },
                            forOnlyNumbersWithDecimal.errors,
                            { message: "Tax % cannot be greater than 50", type: "max" },
                          ]}
                          disabled={editableFields.outward_sgst === false}
                        />
                      </div>
                    </div>
                    <div>
                      <div>
                        <p>
                          IGST <span className="text-red-400">*</span>
                        </p>
                      </div>
                      <div>
                        <Input
                          className="text-xs w-full sm:w-28"
                          placeholder="IGST %"
                          register={register("outward_igst", {
                            required: true,
                            ...forOnlyNumbersWithDecimal.validations,
                            max: {
                              value: 50,
                              message: "Tax % cannot be greater than 50",
                            },
                            validate: {
                              taxSum: () => validateTaxSum("outward", "igst"),
                            },
                            onChange: triggerOutwardTaxValidation,
                          })}
                          fieldError={formHook.formState.errors?.outward_igst}
                          errorMessages={[
                            { message: "IGST % is required", type: "required" },
                            forOnlyNumbersWithDecimal.errors,
                            { message: "Tax % cannot be greater than 50", type: "max" },
                          ]}
                          disabled={editableFields.outward_igst === false}
                        />
                      </div>
                    </div>
                    <div>
                      <div>
                        <p>
                          UTGST <span className="text-red-400">*</span>
                        </p>
                      </div>
                      <div>
                        <Input
                          className="text-xs w-full sm:w-28"
                          placeholder="UTGST %"
                          register={register("outward_ugst", {
                            required: true,
                            ...forOnlyNumbersWithDecimal.validations,
                            max: {
                              value: 50,
                              message: "Tax % cannot be greater than 50",
                            },
                            validate: {
                              taxSum: () => validateTaxSum("outward", "ugst"),
                            },
                            onChange: triggerOutwardTaxValidation,
                          })}
                          fieldError={formHook.formState.errors?.outward_ugst}
                          errorMessages={[
                            { message: "UTGST % is required", type: "required" },
                            forOnlyNumbersWithDecimal.errors,
                            { message: "Tax % cannot be greater than 50", type: "max" },
                          ]}
                          disabled={editableFields.outward_ugst === false}
                        />
                      </div>
                    </div>
                  </div>
                  {getTaxSectionErrorMessage("outward") && (
                    <p className="text-xs text-red-500 mt-2">{getTaxSectionErrorMessage("outward")}</p>
                  )}
                </div>
                <div></div>
                <h2 className="text-sm font-semibold mt-4">Variant Details</h2>
              </div>
              <div>
                <div>
                  <p>
                    Variant Title <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <Input
                    className="text-xs"
                    placeholder="e.g. Royal Canin Persian Adult"
                    register={register("name", {
                      required: true,
                      ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                    })}
                    fieldError={formHook.formState.errors?.name}
                    maxLength={50}
                    errorMessages={[
                      forAllAcceptWithOutStartWithDotAndSpace.errors,
                      { message: "Name is required", type: "required" },
                    ]}
                  />
                </div>
              </div>
              {couponSubCategory?.some(
                (cat: any) => cat?.subcategory_id === formHook.watch("vmsSubcategoryId") && cat?.is_medicine,
              ) && (
                <div>
                  <div className="flex gap-2 items-center">
                    <p>
                      Salt Name <span className="text-red-400">*</span>
                    </p>
                    <div className="text-[#6e7280] bg-[#f5f5f5] rounded-md py-1 px-3 text-xs font-medium">
                      Medicine Only
                    </div>
                  </div>
                  <div className="w-full sm:w-2/6 mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. Altopyrin"
                      register={register("salt_name", {
                        required: true,
                        ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                      })}
                      fieldError={formHook.formState.errors?.salt_name}
                      errorMessages={[
                        forAllAcceptWithOutStartWithDotAndSpace.errors,
                        { message: "Salt Name is required", type: "required" },
                      ]}
                    />
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <p>SKU Name</p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-2/5 mt-2">
                  <div className="flex-1">
                    <Input
                      className={`text-xs bg-gray-100 text-gray-500 cursor-not-allowed`}
                      placeholder="Enter SKU Name"
                      register={register("sku_name")}
                      disabled
                      fieldError={formHook.formState.errors?.sku_name}
                      errorMessages={[]}
                    />
                  </div>
                  <Controller
                    control={formHook.control}
                    name="is_pack"
                    render={({ field }) => (
                      <label className="flex items-center gap-2 text-xs whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer"
                          checked={!!field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                        <span>Same as product name</span>
                      </label>
                    )}
                  />
                </div>
              </div>
              <div>
                <div>
                  <p>
                    Billing Name <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <Input
                    className="text-xs"
                    placeholder="Enter Billing Name"
                    register={register("billing_name", {
                      required: true,
                      ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                    })}
                    fieldError={formHook.formState.errors?.billing_name}
                    errorMessages={[
                      forAllAcceptWithOutStartWithDotAndSpace.errors,
                      { message: "Billing Name is required", type: "required" },
                    ]}
                  />
                </div>
              </div>
              <div>
                <div>
                  <p className="mt-2">Universal Product Code / Barcode</p>
                  <p className="text-xs text-[#6e7280] mt-1">Number & Photo attachment required</p>
                </div>
                {fields?.map((field, index) => {
                  const isLastRow = index === fields?.length - 1;
                  const imageUrl = formHook.getValues(`upcs.${index}.image_url`);
                  let previewSrc: string | null = null;
                  if (imageUrl) {
                    if (typeof imageUrl === "string") {
                      previewSrc = imageUrl;
                    } else if (imageUrl instanceof File) {
                      previewSrc = URL.createObjectURL(imageUrl);
                    }
                  }
                  return (
                    <div key={field?.id} className="mt-4 flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                      <div className="w-full sm:w-1/4">
                        <Input
                          className="text-xs"
                          placeholder="e.g. 123456789012"
                          register={register(`upcs.${index}.upcname`, {
                            required: false,
                          })}
                          fieldError={formHook.formState.errors?.upcs?.[index]?.upcname}
                          errorMessages={[{ message: "", type: "required" }]}
                        />
                      </div>
                      {previewSrc ? (
                        <div
                          className="bg-[#4d4d4d] rounded-md py-2 px-5 text-white cursor-pointer"
                          onClick={() => {
                            setSelectedImage(previewSrc);
                            setIsOpen(true);
                          }}
                        >
                          <FiEye />
                        </div>
                      ) : (
                        <div
                          className="bg-[#4d4d4d] rounded-md py-2 px-5 text-white cursor-pointer"
                          onClick={() => handleUploadClick(index)}
                        >
                          <LuUpload />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        ref={(el) => (fileInputRefs.current[index] = el)}
                        onChange={(e) => handleFileChange(e, index)}
                        style={{ display: "none" }}
                      />
                      {isLastRow ? (
                        <div className="text-[#6d717f] cursor-pointer" onClick={addField}>
                          <FiPlusCircle size={22} />
                        </div>
                      ) : (
                        <div className="text-[#6d717f] cursor-pointer" onClick={() => deleteField(index)}>
                          <RiDeleteBinLine size={22} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div>
                <div>
                  <p>
                    Quantity/package <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <Input
                    className="text-xs"
                    placeholder="e.g. 10"
                    register={register("productQuantity", {
                      required: true,
                      ...forOnlyNumber.validations,
                    })}
                    fieldError={formHook.formState.errors?.productQuantity}
                    errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5">
                <div>
                  <div>
                    <p>
                      Reference MRP <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-60 mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. 500"
                      register={register("reference_mrp", {
                        required: true,
                        ...forOnlyNumber.validations,
                      })}
                      fieldError={formHook.formState.errors?.reference_mrp}
                      errorMessages={[forOnlyNumber.errors, { message: "Reference MRP is required", type: "required" }]}
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <p>
                      Permissible Variance % <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-60 mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. 30%"
                      register={register("permissible_variance", {
                        required: true,
                        ...forOnlyNumber.validations,
                      })}
                      fieldError={formHook.formState.errors?.permissible_variance}
                      errorMessages={[
                        forOnlyNumber.errors,
                        { message: "Permissible Variance % is required", type: "required" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div>
                <p className="mt-2">Lot Numbers</p>
                <p className="text-xs text-[#6e7280] mt-1">Define lot numbers with MRP and expiry details</p>
              </div>
              {lotFields?.map((field, index) => {
                const imageUrl = formHook.getValues(`lots.${index}.image_url`);
                let previewSrc: string | null = null;
                if (imageUrl) {
                  if (typeof imageUrl === "string") {
                    previewSrc = imageUrl;
                  } else if (imageUrl instanceof File) {
                    previewSrc = URL.createObjectURL(imageUrl);
                  }
                }
                return (
                  <div
                    key={field?.id}
                    className="bg-[#f7f7f7] rounded-md p-3 flex flex-col gap-4 sm:flex-row sm:gap-8 sm:items-center w-full"
                  >
                    <div className="flex-1">
                      <div>
                        <p>Lot Number</p>
                      </div>
                      <div className="w-full mt-2">
                        <Input
                          className="text-xs"
                          placeholder="e.g. Batch-001"
                          register={register(`lots.${index}.lot_number`, {
                            required: false,
                            ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                          })}
                          fieldError={formHook.formState.errors?.lots?.[index]?.lot_number as any}
                          errorMessages={[
                            forAllAcceptWithOutStartWithDotAndSpace.errors,
                            { message: "", type: "required" },
                          ]}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div>
                        <p>MRP</p>
                      </div>
                      <div className="w-full mt-2">
                        <Input
                          className="text-xs"
                          placeholder="e.g. $230"
                          register={register(`lots.${index}.mrp`, {
                            required: false,
                            ...forOnlyNumber.validations,
                          })}
                          fieldError={formHook.formState.errors?.lots?.[index]?.mrp as any}
                          errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div>
                        <p>Expiry Date</p>
                      </div>
                      <div className="w-full mt-2">
                        <PWLCalendarComponent
                          showLeftNav={false}
                          errorMessages={[{ message: "", type: "required" }]}
                          register={register(`lots.${index}.date`, {
                            required: false,
                          })}
                          fieldError={formHook.formState.errors?.lots?.[index]?.date as any}
                          defaultSelectedDate={formHook.getValues(`lots.${index}.date`) || undefined}
                          onSelectDate={(date) => {
                            formHook.setValue(`lots.${index}.date`, format(date, "yyyy-MM-dd"));
                            formHook.clearErrors(`lots.${index}.date` as any);
                          }}
                          className="w-[400px]"
                          showFutureYears
                        />
                      </div>
                    </div>
                    {previewSrc ? (
                      <div
                        className="bg-[#4d4d4d] rounded-md py-[8px] px-5 text-white cursor-pointer mt-6"
                        onClick={() => {
                          setSelectedImage(imageUrl);
                          setIsOpen(true);
                        }}
                      >
                        <FiEye />
                      </div>
                    ) : (
                      <div
                        className="bg-[#4d4d4d] rounded-md py-[8px] px-5 text-white cursor-pointer mt-6"
                        onClick={() => handleLotUploadClick(index)}
                      >
                        <LuUpload />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      ref={(el) => (fileInputRefsLot.current[index] = el)}
                      onChange={(e) => {
                        handleLotFileChange(e, index);
                      }}
                      style={{ display: "none" }}
                    />
                    {index === lotFields.length - 1 ? (
                      <div
                        className="text-[#6d717f] cursor-pointer mt-6"
                        onClick={() => {
                          addLotField();
                        }}
                      >
                        <FiPlusCircle size={22} />
                      </div>
                    ) : (
                      <div
                        className="text-[#6d717f] cursor-pointer mt-6"
                        onClick={() => {
                          deleteLotField(index);
                        }}
                      >
                        <RiDeleteBinLine size={22} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div>
                <div>
                  <p>
                    Discount Bucket (%) <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Select Discount Bucket", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("discount_bucket", item?.title);
                      }
                    }}
                    fieldError={formHook.formState.errors?.discount_bucket}
                    register={register("discount_bucket", { required: true })}
                    selectItems={discountItemsList}
                    placeholder="Discount Bucket"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    addAction={{
                      title: `+ ${"Add"}`,
                      callBack: (item: string) => handelCreateItems(item, "DISCOUNT_BUCKET"),
                    }}
                    defaultSelected={discountItemsList?.find(
                      (oc) => oc?.title === (formHook.watch("discount_bucket") ?? 0),
                    )}
                    listBoxClassName="w-full mt-4"
                    className="text-gray-800"
                    containerClassName="w-full"
                  />
                </div>
              </div>
              <div>
                <div>
                  <p>Comments</p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <Input
                    className="text-xs"
                    placeholder="e.g. feed this food only morning"
                    register={register("comment", {
                      required: false,
                      ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                    })}
                    fieldError={formHook.formState.errors?.comment}
                    errorMessages={[forAllAcceptWithOutStartWithDotAndSpace.errors]}
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="w-full sm:w-2/12">
                  <p>
                    Tags <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full sm:w-2/6 mt-2">
                  <div className="border rounded-lg h-auto">
                    <CreatableSelect
                      isMulti
                      value={(formHook.watch("tags") || []).map((t: string) => ({ label: t, value: t }))}
                      onChange={(newValue) => {
                        const cleaned = newValue
                          .map((opt: any) => opt?.value?.trim())
                          .filter((val: string) => val && val.length > 0);
                        formHook.setValue("tags", cleaned);
                      }}
                      className={`text-sm ${formattedTags?.length > 6 && "h-40 overflow-scroll"} border-none`}
                      options={formattedTags}
                      styles={customStyles}
                    />
                  </div>
                </div>
              </div>
              {couponSubCategory?.some(
                (cat: any) => cat?.subcategory_id === formHook.watch("vmsSubcategoryId") && cat?.is_medicine,
              ) && (
                <>
                  <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center w-full">
                    <div className="flex-1">
                      <div>
                        <p>Medicine Consumption Logic Type</p>
                      </div>
                      <div className="w-full mt-2">
                        <SearchableSelectMenu
                          errorMessages={[{ type: "required", message: "" }]}
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue("logic_type", item?.title);
                            } else {
                              formHook.setValue("logic_type", "");
                            }
                          }}
                          fieldError={(formHook?.formState?.errors?.logic_type as FieldError) ?? null}
                          register={register("logic_type", { required: false })}
                          selectItems={logicType}
                          placeholder="Select Medicine Consumption Logic Type"
                          showTooltips={true}
                          showTypedErrors={false}
                          showDropdownIcon={true}
                          defaultSelected={logicType?.filter((oc) => oc?.title === formHook.watch("logic_type"))[0]}
                          listBoxClassName="w-full"
                          className="text-gray-800"
                          containerClassName="w-full"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div>
                        <p>Medicine Consumption Logic Formula</p>
                      </div>
                      <div className="w-full mt-2">
                        <Input
                          className="text-xs"
                          placeholder="Enter Medicine Consumption Logic Formula"
                          register={register("logic_formula", {
                            required:
                              formHook.watch("logic_type") === "Static"
                                ? "This field is required for Static logic type"
                                : false,
                            ...(formHook.watch("logic_type") === "Static"
                              ? forOnlyNumber.validations
                              : forOnlyNumbersWithDecimal.validations),
                          })}
                          value={formHook.watch("logic_formula") ?? ""}
                          fieldError={formHook.formState.errors?.logic_formula}
                          errorMessages={[
                            formHook.watch("logic_type") === "Weight"
                              ? forOnlyNumber.errors
                              : forOnlyNumbersWithDecimal.errors,
                          ]}
                          maxLength={10}
                          disabled={!isEditMode && editableFields.logic_formula === false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center w-full">
                    <div className="flex-1">
                      <div>
                        <p>Medicine Consumption Unit</p>
                      </div>
                      <div className="w-full mt-2">
                        <SearchableSelectMenu
                          errorMessages={[{ type: "required", message: "" }]}
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue("unit", item?.title);
                            } else {
                              formHook.setValue("unit", "");
                            }
                          }}
                          fieldError={(formHook?.formState?.errors?.unit as FieldError) ?? null}
                          register={register("unit", { required: false })}
                          selectItems={medicineConsumptionListItems}
                          placeholder="Select Medicine Consumption Unit"
                          showTooltips={true}
                          showTypedErrors={false}
                          showDropdownIcon={true}
                          defaultSelected={
                            medicineConsumptionListItems?.filter((oc) => oc?.title === formHook.watch("unit"))[0]
                          }
                          listBoxClassName="w-full"
                          className="text-gray-800"
                          containerClassName="w-full"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div>
                        <p>Medicine Weight Size Per Qty</p>
                      </div>
                      <div className="w-full mt-2">
                        <Input
                          className="text-xs"
                          placeholder="Enter Medicine Weight Size Per Qty"
                          register={register("weight_size", {
                            required: false,
                            ...forOnlyNumber.validations,
                          })}
                          fieldError={formHook.formState.errors?.weight_size}
                          errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-10 w-full">
                <div className="flex items-center justify-between gap-5 mt-2 flex-1 sm:justify-start">
                  <div>
                    <p>Allow shrinkage on issuance</p>
                  </div>
                  <div>
                    <ToggleWithIcon
                      onToggleChange={(status) => {
                        formHook.setValue("shrinkage", status);
                      }}
                      toggleState={formHook.watch("shrinkage")}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-5 mt-2 flex-1 sm:justify-start">
                  <div>
                    <p>Shrinkable</p>
                  </div>
                  <div>
                    <ToggleWithIcon
                      onToggleChange={(status) => {
                        formHook.setValue("is_shrinkable", status);
                      }}
                      toggleState={formHook.watch("is_shrinkable")}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-5 mt-2 flex-1 sm:justify-start">
                  <div>
                    <p>Visible for PO</p>
                  </div>
                  <div>
                    <ToggleWithIcon
                      onToggleChange={(visibility) => {
                        formHook.setValue("visibility", visibility);
                      }}
                      toggleState={formHook.watch("visibility")}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-5 mt-2 flex-1 sm:justify-start">
                  <div>
                    <p>Visible on VMS</p>
                  </div>
                  <div>
                    <ToggleWithIcon
                      onToggleChange={(status) => {
                        formHook.setValue("status", status);
                      }}
                      toggleState={formHook.watch("status")}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={loader}
                  className={clsx(
                    PRIMARY_BUTTON,
                    "w-full sm:w-auto disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed",
                  )}
                >
                  {loader ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      {productDetails ? "Updating..." : "Saving..."}
                    </div>
                  ) : productDetails ? (
                    "Update Changes"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {isOpen && selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[400px]">
            <BarcodeViewModal selectedImage={selectedImage} onClose={() => setIsOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralDetailsFormComponent;
